/**
 * Ventanas de apoyo de **Gestión → Productos**: categorías con subcategorías y
 * precios programados.
 *
 * Son el puerto de las pantallas `menu/producto/{categoria,subcategoria,
 * precio_programado}.php` del panel anterior; se abren desde la propia pantalla
 * de productos y no desde el menú, que ya tenía demasiados accesos.
 *
 * Los **proveedores** ya no se administran aquí: viven en
 * *Compras → Proveedores* (`/gestion/compras/proveedores`), que es su sitio.
 *
 * Reglas conservadas:
 * - Los nombres se guardan en mayúsculas (lo hacía el modelo original).
 * - Un precio programado **sólo se cancela mientras está `pendiente`** y su
 *   fecha debe ser futura: lo aplica el cron del servidor.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';

import { fechaHora, hoyIso, monto } from '../gestion-formato';
import {
  useCancelarPrecio,
  useCategorias,
  useGuardarCategoria,
  useGuardarSubcategoria,
  usePreciosProgramados,
  useProgramarPrecio,
} from '../productos.api';
import type { ProductoGestion } from '../gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const ACCION_CAMPO = 'flex items-end gap-2';

const TONO_PRECIO: Record<string, 'success' | 'warning' | 'neutral' | 'danger'> = {
  pendiente: 'warning',
  aplicado: 'success',
  cancelado: 'neutral',
  aplicado_parcial: 'danger',
};

interface VentanaProps {
  abierto: boolean;
  onClose: () => void;
  onAviso: (mensaje: string) => void;
}

/* ------------------------------------------------------------------ *
 * Categorías y subcategorías
 * ------------------------------------------------------------------ */

export function CategoriasDialog({ abierto, onClose, onAviso }: VentanaProps) {
  const consulta = useCategorias(abierto);
  const guardarCategoria = useGuardarCategoria();
  const guardarSubcategoria = useGuardarSubcategoria();

  const [categoria, setCategoria] = useState({ id: undefined as number | undefined, nombre: '', descripcion: '' });
  const [subcategoria, setSubcategoria] = useState({
    id: undefined as number | undefined,
    categoria_id: 0,
    nombre: '',
    descripcion: '',
  });
  const [error, setError] = useState<string | null>(null);

  const categorias = consulta.data?.categorias ?? [];

  const guardar = (id: number | undefined, nombre: string, descripcion: string, estado?: number) => {
    setError(null);
    guardarCategoria.mutate(
      { id, nombre, descripcion, estado },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          setCategoria({ id: undefined, nombre: '', descripcion: '' });
        },
        onError: (fallo) => setError(fallo instanceof ApiError ? fallo.message : 'No se pudo guardar.'),
      },
    );
  };

  const guardarSub = () => {
    setError(null);
    guardarSubcategoria.mutate(
      {
        id: subcategoria.id,
        categoria_id: subcategoria.categoria_id,
        nombre: subcategoria.nombre,
        descripcion: subcategoria.descripcion,
      },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          setSubcategoria({
            id: undefined,
            categoria_id: subcategoria.categoria_id,
            nombre: '',
            descripcion: '',
          });
        },
        onError: (fallo) => setError(fallo instanceof ApiError ? fallo.message : 'No se pudo guardar.'),
      },
    );
  };

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      size="xl"
      title="Categorías y subcategorías"
      description="Los nombres se guardan en mayúsculas. Una categoría con productos no se puede borrar, se desactiva."
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {error ? (
        <Alert className="mt-4" variant="error" title="No se pudo guardar">
          {error}
        </Alert>
      ) : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <section className="space-y-3">
          <div className={ACCION_CAMPO}>
            <Input
              label="Categoría"
              value={categoria.nombre}
              onChange={(evento) => setCategoria((previo) => ({ ...previo, nombre: evento.target.value }))}
            />
            <Input
              label="Descripción"
              value={categoria.descripcion}
              onChange={(evento) => setCategoria((previo) => ({ ...previo, descripcion: evento.target.value }))}
            />
            <Button
              size="sm"
              loading={guardarCategoria.isPending}
              onClick={() => guardar(categoria.id, categoria.nombre, categoria.descripcion)}
            >
              {categoria.id === undefined ? 'Añadir' : 'Guardar'}
            </Button>
          </div>

          {consulta.isLoading ? <Skeleton className="h-40 w-full" /> : null}

          <ul className="max-h-[45vh] space-y-2 overflow-auto">
            {categorias.map((item) => (
              <li key={item.id} className="rounded-card border border-ink-100 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold text-ink-900">{item.nombre}</p>
                    <p className="text-xs text-ink-500">
                      {item.descripcion ?? 'Sin descripción'} · {item.productos} producto
                      {item.productos === 1 ? '' : 's'} · {item.subcategorias.length} subcategoría
                      {item.subcategorias.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={item.estado ? 'success' : 'neutral'}>
                      {item.estado ? 'ACTIVA' : 'INACTIVA'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCategoria({ id: item.id, nombre: item.nombre, descripcion: item.descripcion ?? '' })}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        guardar(item.id, item.nombre, item.descripcion ?? '', item.estado ? 0 : 1)
                      }
                    >
                      {item.estado ? 'Desactivar' : 'Activar'}
                    </Button>
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {item.subcategorias.map((sub) => (
                    <button
                      key={sub.id}
                      className="rounded-full border border-ink-200 px-2 py-0.5 text-xs text-ink-600 hover:border-brand-400 hover:text-brand-600"
                      title="Editar subcategoría"
                      onClick={() =>
                        setSubcategoria({
                          id: sub.id,
                          categoria_id: item.id,
                          nombre: sub.nombre,
                          descripcion: sub.descripcion ?? '',
                        })
                      }
                    >
                      {sub.nombre}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-3 rounded-card border border-ink-100 bg-ink-50/40 p-3">
          <h3 className="text-sm font-semibold text-ink-800">Subcategoría</h3>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Categoría</span>
            <Select
              className={CONTROL}
              value={subcategoria.categoria_id}
              onChange={(evento) =>
                setSubcategoria((previo) => ({ ...previo, categoria_id: Number(evento.target.value) }))
              }
            >
              <option value={0}>SELECCIONE</option>
              {categorias.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nombre}
                </option>
              ))}
            </Select>
          </label>

          <Input
            label="Nombre"
            value={subcategoria.nombre}
            onChange={(evento) => setSubcategoria((previo) => ({ ...previo, nombre: evento.target.value }))}
          />
          <Input
            label="Descripción"
            value={subcategoria.descripcion}
            onChange={(evento) => setSubcategoria((previo) => ({ ...previo, descripcion: evento.target.value }))}
          />

          <Button
            size="sm"
            loading={guardarSubcategoria.isPending}
            onClick={guardarSub}
            disabled={!subcategoria.categoria_id || !subcategoria.nombre.trim()}
          >
            {subcategoria.id === undefined ? 'Añadir subcategoría' : 'Guardar subcategoría'}
          </Button>

          {subcategoria.id !== undefined ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setSubcategoria({ id: undefined, categoria_id: subcategoria.categoria_id, nombre: '', descripcion: '' })
              }
            >
              Nueva subcategoría
            </Button>
          ) : null}
        </section>
      </div>
    </Dialog>
  );
}

function cnVacio() {
  return 'rounded-card border border-dashed border-ink-200 p-6 text-center text-sm text-ink-500';
}

/* ------------------------------------------------------------------ *
 * Precios programados
 * ------------------------------------------------------------------ */

export function PreciosProgramadosDialog({
  abierto,
  onClose,
  onAviso,
  productos,
}: VentanaProps & { productos: ProductoGestion[] }) {
  const consulta = usePreciosProgramados(abierto);
  const programar = useProgramarPrecio();
  const cancelar = useCancelarPrecio();

  const [formulario, setFormulario] = useState({
    producto_id: 0,
    precio_venta_nuevo: '',
    precio_compra_nuevo: '',
    fecha_programada: '',
    motivo: '',
  });
  const [error, setError] = useState<string | null>(null);

  const programados = consulta.data?.programados ?? [];
  const pendientes = consulta.data?.pendientes ?? 0;

  const enviar = () => {
    setError(null);
    programar.mutate(
      {
        producto_id: formulario.producto_id,
        precio_venta_nuevo: Number(formulario.precio_venta_nuevo),
        precio_compra_nuevo: formulario.precio_compra_nuevo
          ? Number(formulario.precio_compra_nuevo)
          : undefined,
        fecha_programada: formulario.fecha_programada,
        motivo: formulario.motivo.trim() || undefined,
      },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.mensaje);
          setFormulario({
            producto_id: 0,
            precio_venta_nuevo: '',
            precio_compra_nuevo: '',
            fecha_programada: '',
            motivo: '',
          });
        },
        onError: (fallo) => setError(fallo instanceof ApiError ? fallo.message : 'No se pudo programar.'),
      },
    );
  };

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      size="xl"
      title="Precios programados"
      description="El precio programado queda pendiente y lo aplica el cron del servidor en su fecha; sólo se cancela mientras siga pendiente."
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      <Alert variant="info" title="Cómo funciona">
        <span className={ICONO_EN_ALERTA}>
          Un precio programado no cambia el producto al instante: queda en estado <strong>pendiente</strong> y
          el cron del servidor lo aplica a su fecha, guardándolo también en el historial del producto.
          Ahora mismo hay {pendientes} pendiente{pendientes === 1 ? '' : 's'}.
        </span>
      </Alert>

      {error ? (
        <Alert className="mt-4" variant="error" title="No se pudo programar">
          {error}
        </Alert>
      ) : null}

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Producto</span>
          <Select
            className={CONTROL}
            value={formulario.producto_id}
            onChange={(evento) =>
              setFormulario((previo) => ({ ...previo, producto_id: Number(evento.target.value) }))
            }
          >
            <option value={0}>SELECCIONE</option>
            {productos.map((producto) => (
              <option key={producto.id} value={producto.id}>
                {producto.nombre}
              </option>
            ))}
          </Select>
        </label>

        <Input
          label="Precio de venta nuevo (S/)"
          type="number"
          step="0.001"
          value={formulario.precio_venta_nuevo}
          onChange={(evento) =>
            setFormulario((previo) => ({ ...previo, precio_venta_nuevo: evento.target.value }))
          }
        />
        <Input
          label="Precio de compra nuevo (S/, opcional)"
          type="number"
          step="0.001"
          value={formulario.precio_compra_nuevo}
          onChange={(evento) =>
            setFormulario((previo) => ({ ...previo, precio_compra_nuevo: evento.target.value }))
          }
        />
        <Input
          label="Fecha y hora"
          type="datetime-local"
          min={hoyIso()}
          value={formulario.fecha_programada}
          onChange={(evento) =>
            setFormulario((previo) => ({ ...previo, fecha_programada: evento.target.value }))
          }
        />
        <Input
          label="Motivo (opcional)"
          containerClassName="sm:col-span-2"
          value={formulario.motivo}
          onChange={(evento) => setFormulario((previo) => ({ ...previo, motivo: evento.target.value }))}
        />

        <div className="flex items-end">
          <Button
            size="sm"
            loading={programar.isPending}
            disabled={!formulario.producto_id || !formulario.precio_venta_nuevo || !formulario.fecha_programada}
            leftIcon={<Plus className="size-4" />}
            onClick={enviar}
          >
            Programar precio
          </Button>
        </div>
      </div>

      {consulta.isLoading ? <Skeleton className="mt-4 h-40 w-full" /> : null}

      {programados.length > 0 ? (
        <div className="mt-4 max-h-[40vh] overflow-auto rounded-card border border-ink-100">
          <table className="w-full border-collapse text-[13px]">
            <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
              <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                <th className="px-2 py-2">Producto</th>
                <th className="px-2 py-2 text-right">Precio nuevo</th>
                <th className="px-2 py-2 text-right">Precio actual</th>
                <th className="px-2 py-2">Fecha programada</th>
                <th className="px-2 py-2">Estado</th>
                <th className="px-2 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {programados.map((item) => (
                <tr key={item.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                  <td className="max-w-[14rem] px-2 py-2">
                    <p className="truncate font-semibold text-ink-900" title={item.producto ?? ''}>
                      {item.producto ?? `Producto ${item.producto_id}`}
                    </p>
                    <p className="text-xs text-ink-500">{item.motivo ?? 'Sin motivo'}</p>
                  </td>
                  <td className="px-2 py-2 text-right font-semibold whitespace-nowrap">
                    {monto(item.precio_venta_nuevo)}
                    <p className="text-xs font-normal text-ink-500">
                      antes {monto(item.precio_venta_anterior)}
                    </p>
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {item.precio_venta_actual === null ? '—' : monto(item.precio_venta_actual)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {fechaHora(item.fecha_programada)}
                    {item.fecha_ejecucion ? (
                      <p className="text-xs text-ink-500">aplicado {fechaHora(item.fecha_ejecucion)}</p>
                    ) : null}
                  </td>
                  <td className="px-2 py-2">
                    <Badge variant={TONO_PRECIO[item.estado ?? ''] ?? 'neutral'}>
                      {(item.estado ?? '—').toUpperCase()}
                    </Badge>
                    <p className="text-xs text-ink-500">{item.usuario ?? '—'}</p>
                  </td>
                  <td className="px-2 py-2 text-right">
                    {item.estado === 'pendiente' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={cancelar.isPending}
                        onClick={() =>
                          cancelar.mutate(item.id, {
                            onSuccess: (respuesta) => onAviso(respuesta.mensaje),
                            onError: (fallo) =>
                              setError(
                                fallo instanceof ApiError ? fallo.message : 'No se pudo cancelar.',
                              ),
                          })
                        }
                      >
                        Cancelar
                      </Button>
                    ) : (
                      <span className="text-xs text-ink-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !consulta.isLoading ? (
        <p className={`mt-4 ${cnVacio()}`}>No hay precios programados.</p>
      ) : null}
    </Dialog>
  );
}
